import { z } from 'zod';
import type { FilterFieldType, FilterGroup } from './filter';
import { GS_LEDGER_DEFAULT_COLUMN_KEYS } from './gs-ledger-columns';
import { PrimaryWorkspaceLinkSchema } from './primary-workspace-link';
import type { FlowRole } from './role';
import { meChipGroup } from './role-me-chip';
import type { FlowView } from './view';

/**
 * Account view wire contract: the field catalog, the row schema the
 * /api/flow/accounts endpoints return, the seeded v0 presets, and the
 * cross-dimension toggle-chip FilterGroups (Rob's headline ask).
 *
 * Field keys must match the SQL aliases in src/api/flow.ts's
 * ACCOUNT_FIELD_MAP — flow-account-contract.test.ts asserts alignment.
 *
 * Naming (per Views doc 3927): GTME Owner = the sf_owner_id join;
 * ClayDR (the prototype's "SDR" column) = clay_dr_id; Growth Strategist
 * = growth_strategist_id.
 */

// ── Field catalog ────────────────────────────────────────────────────────────

export interface AccountFieldDef {
  key: string;
  label: string;
  type: FilterFieldType;
  /** Not offered in the filter builder (computed/link columns). */
  noFilter?: boolean;
  /** A closed picklist's values, offered as-is by the filter builder. */
  values?: readonly string[];
}

/**
 * The Salesforce health ratings an account can carry. A closed set, so the
 * health filter offers exactly these and compares them exactly, which lets
 * the health_rating index serve it.
 */
export const ACCOUNT_HEALTH_VALUES = ['Green', 'Yellow', 'Red'] as const;

export const ACCOUNT_FIELDS: AccountFieldDef[] = [
  // "Account Name", not "Account" (U5, QA): a bare "Account" header reads as
  // a link to the account view rather than the account's name. The contact
  // catalog's `account_name` carries the same label for the same reason.
  { key: 'name',                      label: 'Account Name',         type: 'text' },
  { key: 'propensity_score',          label: 'Propensity',           type: 'number' },
  { key: 'fit_score',                 label: 'Fit',                  type: 'number' },
  { key: 'claydr_name',               label: 'ClayDR',               type: 'text' },
  { key: 'last_opportunity_stage',    label: 'Pipeline Stage',       type: 'picklist' },
  { key: 'open_pipeline',             label: 'Open Pipeline',        type: 'number' },
  { key: 'lim_name',                  label: 'Last Signal',          type: 'text' },
  { key: 'lim_date',                  label: 'Signal Date',          type: 'date' },
  { key: 'hq',                        label: 'HQ',                   type: 'text', noFilter: true },
  { key: 'owner_name',                label: 'GTME Owner',           type: 'text' },
  { key: 'product_tier',              label: 'Product',              type: 'picklist' },
  { key: 'plan_interval',             label: 'Plan',                 type: 'picklist' },
  { key: 'l90_signal_count',          label: 'L90 Signals',          type: 'number' },
  { key: 'last_gtme_meeting',         label: 'Last GTME Meeting',    type: 'date' },
  { key: 'last_gs_meeting',           label: 'Last GS Meeting',      type: 'date' },
  { key: 'last_claydr_meeting',       label: 'Last CDR Meeting',     type: 'date' },
  { key: 'number_of_workspace',       label: '# Workspaces',         type: 'number' },
  { key: 'workspaces_created_last_90_days', label: 'Workspaces (L90)', type: 'number' },
  { key: 'tech_stack',                label: 'Tech Stack (1st party)', type: 'text' },
  { key: 'technologies_used',         label: 'Tech Stack (scraped)', type: 'text' },
  // Catalog extras (add-from-catalog / Other-fields tab)
  { key: 'account_type',              label: 'Type',                 type: 'picklist' },
  { key: 'industry',                  label: 'Industry',             type: 'text' },
  { key: 'number_of_employees',       label: 'Employees',            type: 'number' },
  { key: 'engagement_score_90_days',  label: 'Engagement (L90)',     type: 'number' },
  { key: 'engagement_status_90_days', label: 'Engagement Status',    type: 'picklist' },
  { key: 'number_of_open_opp',        label: '# Open Opps',          type: 'number' },
  { key: 'arr',                       label: 'ARR',                  type: 'number' },
  { key: 'arr_opportunity',           label: 'ARR Opportunity',      type: 'number' },
  { key: 'pcv',                       label: 'PCV',                  type: 'number' },
  { key: 'sales_tier',                label: 'Sales Tier',           type: 'picklist' },
  { key: 'account_fit',               label: 'Account Fit',          type: 'picklist' },
  { key: 'fortune_500_rank',          label: 'F500 Rank',            type: 'number' },
  { key: 'gs_name',                   label: 'Growth Strategist',    type: 'text' },
  { key: 'l90_website_visits',        label: 'L90 Web Visits',       type: 'number' },
  { key: 'l90_active_clay_users',     label: 'L90 Active Clay Users', type: 'number' },
  { key: 'company_segment',           label: 'Segment',              type: 'picklist' },
  { key: 'billing_country',           label: 'Country',              type: 'text' },
  { key: 'billing_city',              label: 'City',                 type: 'text' },
  // Bucket A/E follow-up (U5, 2026-08-27): `created_date` and `region` both
  // populate on the first hourly object-sync after deploy — Region__c ships
  // from Middleman as of terra-middleware PR #257 (2026-08-27).
  { key: 'created_date',              label: 'Created Date',         type: 'date' },
  { key: 'region',                    label: 'Region',               type: 'text' },
  // Growth Strategist fields. `health` is a closed picklist (see
  // ACCOUNT_HEALTH_VALUES). `red_reason` is a Salesforce multiselect kept as
  // its raw `;`-joined text, so text operators match each pick.
  { key: 'health',                    label: 'Health',               type: 'picklist', values: ACCOUNT_HEALTH_VALUES },
  { key: 'red_reason',                label: 'Red Reason',           type: 'text' },
  // Shown only: a plain entry would also offer a filter and a sort, and
  // there is no `ACCOUNT_FIELD_MAP` expression or index plan behind either.
  { key: 'risk_reasons',              label: 'Risk Reasons',         type: 'text', noFilter: true },
  { key: 'renewal_date',              label: 'Renewal Date',         type: 'date' },
  { key: 'next_gs',                   label: 'Next GS',              type: 'text' },
  { key: 'next_gs_assign_date',       label: 'Next GS Date',         type: 'date' },
  { key: 'gs_arr',                    label: 'Contracted ARR',       type: 'number' },
  { key: 'contract_end',              label: 'Contract Ends',        type: 'date' },
  { key: 'credit_pace',               label: 'Credit Pacing',        type: 'number' },
  { key: 'credits_l30',               label: 'Credit Spent L30',     type: 'number' },
  { key: 'lim_type',                  label: 'Signal Type',          type: 'picklist' },
  { key: 'lim_subtype',               label: 'Signal Subtype',       type: 'picklist' },
  { key: 'lim_description',           label: 'Signal Description',   type: 'text' },
  { key: 'lim_link',                  label: 'Signal Link',          type: 'text', noFilter: true },
  { key: 'suppressed',                label: 'Suppressed',           type: 'boolean' },
  { key: 'is_agency',                 label: 'Is Agency',            type: 'boolean' },
  { key: 'email_domain_link',         label: 'Domain',               type: 'text' },
  { key: 'linkedin',                  label: 'LinkedIn',             type: 'text', noFilter: true },
  // Bucket A/E follow-up (U6, 2026-08-27) [ASSUMED DEFAULT — Spencer has NOT
  // confirmed which links/surface despite two asks; see the plan's Key
  // Technical Decision 10]: four link columns over already-synced
  // `salesforce_user.meeting_link`/`customer_meeting_link`, joined through the
  // existing `owner_user` (GTME)/`claydr_user` (CDR) LEFT JOINs in
  // src/api/flow.ts. `noFilter: true` follows the `hq`/`lim_link`/`linkedin`
  // convention above — link/computed columns aren't filterable. Only the two
  // non-customer variants are default-visible (ACCOUNT_DEFAULT_COLUMNS
  // below); the customer variants are catalog-only (add-from-catalog).
  { key: 'owner_meeting_link',           label: 'GTME Meeting Link',          type: 'text', noFilter: true },
  { key: 'owner_customer_meeting_link',  label: 'GTME Customer Meeting Link', type: 'text', noFilter: true },
  { key: 'claydr_meeting_link',          label: 'CDR Meeting Link',           type: 'text', noFilter: true },
  { key: 'claydr_customer_meeting_link', label: 'CDR Customer Meeting Link',  type: 'text', noFilter: true },
  { key: 'sfdc_link',                 label: 'SFDC',                 type: 'text', noFilter: true },
  // Tracker (U7 / R6) — Terra-only per-record state joined from
  // flow_record_trackers, identical on both entities. Catalogued here so they
  // can be SHOWN as columns and grouped on; the filter-picker section
  // ('Tracker') is declared once in src/shared/flow/filter-fields.ts.
  //
  // `tracker`/`tracker_status` deliberately NOT columns any more (U7 tracker
  // polish) — see the identical note in contact-view.ts's CONTACT_FIELDS.
  // `tracker` stays groupable (TRACKER_FILTER_FIELDS) and `tracker_status`
  // stays filterable for any pre-existing saved view (ACCOUNT_FIELD_MAP);
  // this is a columns-only removal, graceful for stale saved-view columns.
  { key: 'tracker_date',              label: 'Tracker Date',         type: 'date' },
  { key: 'tracker_note',              label: 'Tracker Note',         type: 'text' },

  // ── Bucket A/E U5a — primary workspace + customer lookalikes ──────────────
  // These four are `noFilter: true`, which is what keeps them out of
  // ACCOUNT_FILTER_FIELDS (fromColumnFields drops noFilter entries) and
  // therefore out of ACCOUNT_FIELD_MAP, whose bidirectional contract test
  // demands a catalog twin for every mapped key. That is the correct shape,
  // not a shortcut: the lookalike name/date columns come off three LEFT JOINs
  // onto the TARGET accounts (Decision 7), and nobody filters a book of
  // accounts by "lookalike 2's name contains…" — they are read on the
  // drill-down Fields card. Same treatment `lim_link`/`linkedin`/`sfdc_link`
  // already get.
  //
  // `account_primary_workspace` is deliberately NOT catalogued: it is an
  // OBJECT on the wire (`{ workspaceId, url }`, computed at row-shaping time
  // from the raw `Primary_Workspace_Link__c` anchor — Decision 4), and
  // AccountFlowTable turns every catalog entry into a generic column whose
  // default cell stringifies the value. It stays wire-only (like
  // `account_notes` / `engagement_summary_90_days`) until U6 gives it a real
  // cell renderer. The lookalike PITCH text is wire-only for the same
  // "read it on the Fields card, not in a 40-column table" reason.
  { key: 'account_lookalike_1_name',    label: 'Lookalike 1',          type: 'text', noFilter: true },
  { key: 'account_lookalike_2_name',    label: 'Lookalike 2',          type: 'text', noFilter: true },
  { key: 'account_lookalike_3_name',    label: 'Lookalike 3',          type: 'text', noFilter: true },
  { key: 'account_lookalike_last_enriched', label: 'Lookalikes Enriched', type: 'date', noFilter: true },
];

export const ACCOUNT_FIELD_BY_KEY: Record<string, AccountFieldDef> =
  Object.fromEntries(ACCOUNT_FIELDS.map(f => [f.key, f]));

// ── Row wire schema (trust boundary for /api/flow/accounts) ─────────────────

/**
 * One account row. `id` = the SFDC 18-char account id
 * (salesforce_account_id — unique + non-null; also the drill-down route
 * param and keyset tiebreak). Dates are ISO strings; numerics plain numbers.
 * Carries the full synced field surface so the drill-down's "Other fields"
 * tab is pure rendering.
 */
export const AccountRowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  propensity_score: z.number().nullable(),
  fit_score: z.number().nullable(),
  owner_name: z.string().nullable(),
  claydr_name: z.string().nullable(),
  gs_name: z.string().nullable(),
  last_opportunity_stage: z.string().nullable(),
  open_pipeline: z.number().nullable(),
  lim_name: z.string().nullable(),
  lim_date: z.string().nullable(),
  lim_type: z.string().nullable(),
  lim_subtype: z.string().nullable(),
  lim_description: z.string().nullable(),
  lim_link: z.string().nullable(),
  billing_city: z.string().nullable(),
  billing_state: z.string().nullable(),
  billing_country: z.string().nullable(),
  // Bucket A/E follow-up (U5, 2026-08-27): `created_date` and `region` both
  // populate on the first hourly object-sync after deploy (Region__c ships
  // from Middleman as of terra-middleware PR #257).
  created_date: z.string().nullable(),
  region: z.string().nullable(),
  // Growth Strategist fields, always selected. The two dates are `YYYY-MM-DD`
  // calendar days.
  health: z.string().nullable(),
  red_reason: z.string().nullable(),
  /** Salesforce renewal risk reasons, a `;`-joined multiselect. Optional so a row without the key still parses. */
  risk_reasons: z.string().nullable().optional(),
  renewal_date: z.string().nullable(),
  next_gs: z.string().nullable(),
  next_gs_assign_date: z.string().nullable(),
  /** Salesforce contracted ARR. A different figure from `arr`. */
  gs_arr: z.number().nullable(),
  // The three heavy fields, read from other tables and carried on every row.
  /** End date of the primary contract-terms row, a `YYYY-MM-DD` calendar day, null when the account has no contract-terms row or its primary row has no value. */
  contract_end: z.string().nullable(),
  /** The primary contract-terms row's pacing ratio, null when the account has no contract-terms row or its primary row has no value. */
  credit_pace: z.number().nullable(),
  /** Credits spent over the last 30 UTC days, null when the window holds none. */
  credits_l30: z.number().nullable(),
  product_tier: z.string().nullable(),
  plan_interval: z.string().nullable(),
  account_type: z.string().nullable(),
  industry: z.string().nullable(),
  number_of_employees: z.number().nullable(),
  l90_signal_count: z.number().nullable(),
  l90_website_visits: z.number().nullable(),
  l90_active_clay_users: z.number().nullable(),
  engagement_score_90_days: z.number().nullable(),
  engagement_status_90_days: z.string().nullable(),
  engagement_summary_90_days: z.string().nullable(),
  engagement_channels: z.string().nullable(),
  number_of_open_opp: z.number().nullable(),
  number_of_won_opp: z.number().nullable(),
  number_of_open_renewals: z.number().nullable(),
  number_of_open_new_biz_opps: z.number().nullable(),
  arr: z.number().nullable(),
  arr_opportunity: z.number().nullable(),
  pcv: z.number().nullable(),
  sales_tier: z.string().nullable(),
  account_fit: z.string().nullable(),
  /** US Fortune 500 rank (1 is the largest), null for every account outside the list. */
  fortune_500_rank: z.number().nullable(),
  company_segment: z.string().nullable(),
  suppressed: z.boolean().nullable(),
  last_gtme_meeting: z.string().nullable(),
  last_gs_meeting: z.string().nullable(),
  last_claydr_meeting: z.string().nullable(),
  account_notes: z.string().nullable(),
  account_description: z.string().nullable(),
  fit_score_summary: z.string().nullable(),
  tech_stack: z.string().nullable(),
  technologies_used: z.string().nullable(),
  number_of_workspace: z.number().nullable(),
  workspaces_created_last_90_days: z.number().nullable(),
  linkedin: z.string().nullable(),
  email_domain_link: z.string().nullable(),

  // ── Bucket A/E follow-up (U6, 2026-08-27): GTME/CDR meeting links ────────
  // Already-synced `salesforce_user.meeting_link`/`customer_meeting_link`,
  // joined through the `owner_user`/`claydr_user` LEFT JOINs (src/api/flow.ts)
  // — `.optional()` for the usual deploy-skew reason (Decision 9). Values look
  // like `https://meet.clay.com/meet/<name>/inbound`; customer links are
  // sparser and null more often.
  owner_meeting_link: z.string().nullable().optional(),
  owner_customer_meeting_link: z.string().nullable().optional(),
  claydr_meeting_link: z.string().nullable().optional(),
  claydr_customer_meeting_link: z.string().nullable().optional(),

  // ── Bucket A/E U5a: primary workspace (Decision 4) ───────────────────────
  // PARSED server-side from `salesforce_account.primary_workspace_link` (a
  // `HYPERLINK()` formula stored as raw anchor HTML). The raw HTML never
  // reaches the wire and the frontend never re-parses it — `src/api/flow.ts`'s
  // `toAccountWireRow` swaps the column for this object. Null whenever the
  // link is absent or carries no numeric id.
  //
  // Nullable AND optional for the usual deploy-skew reason (Decision 9): a new
  // frontend reading an old backend's response must still parse.
  account_primary_workspace: PrimaryWorkspaceLinkSchema.nullable().optional(),

  // ── Bucket A/E U5a: customer lookalikes (Decision 7) ─────────────────────
  // Slot N is the viewed account's `account_lookalike_N_id` → a TARGET account
  // row in the same mirror. `_name` and `_use_case` are the TARGET's own
  // columns: every account's `lookalike_use_case_*` text describes ITSELF
  // (live-verified on Snyk → Semgrep), so the pitch under a lookalike's name
  // is that lookalike's pitch, never the viewed account's. `_use_case` is the
  // target's FIRST populated slot.
  //
  // `_id` survives when the target is absent from the mirror (dangling SF
  // reference, or a tombstoned target the join filters out) — the UI skips a
  // slot whose `_name` is null rather than rendering a bare id.
  account_lookalike_1_id: z.string().nullable().optional(),
  account_lookalike_1_name: z.string().nullable().optional(),
  account_lookalike_1_use_case: z.string().nullable().optional(),
  account_lookalike_2_id: z.string().nullable().optional(),
  account_lookalike_2_name: z.string().nullable().optional(),
  account_lookalike_2_use_case: z.string().nullable().optional(),
  account_lookalike_3_id: z.string().nullable().optional(),
  account_lookalike_3_name: z.string().nullable().optional(),
  account_lookalike_3_use_case: z.string().nullable().optional(),
  /** The VIEWED account's own `Last_Use_Case_Enrichment_Date__c` — the
   * "Last enrichment: <date>" footer under the lookalikes block. */
  account_lookalike_last_enriched: z.string().nullable().optional(),

  // ── Tracker (U7 / R6) — LEFT JOIN flow_record_trackers ────────────────────
  // Contact twin of these fields: see ContactRowSchema for why `tracker_rank`
  // is on the wire (keyset cursors are built from the PARSED row, so an ORDER
  // BY level the schema strips would round-trip as null) and why these five
  // are `.optional()` rather than required-nullable for now.
  tracker: z.string().nullable().optional(),
  tracker_status: z.string().nullable().optional(),
  /** `YYYY-MM-DD` — a DATE column, SELECTed as text (no clock, no zone). */
  tracker_date: z.string().nullable().optional(),
  tracker_note: z.string().nullable().optional(),
  tracker_rank: z.number().nullable().optional(),
});
export type AccountRow = z.infer<typeof AccountRowSchema>;

/**
 * One account on the detail read: the list row plus nine Growth Strategist
 * fields only the account page reads. The list never selects them (it is the
 * hot path and no list column shows them), so they live here rather than on
 * `AccountRowSchema`. Every one is nullable and optional, as is
 * `risk_reasons` on the list row: during a rolling deploy a backend deployed
 * before these fields existed answers without them, and its payload still
 * parses.
 */
export const AccountDetailRowSchema = AccountRowSchema.extend({
  /** The Salesforce health write-up, a "<Rating>: <headline>" line then labelled sections. */
  health_descr: z.string().nullable().optional(),
  /** When the health rating last changed, an ISO timestamp. */
  health_updated_at: z.string().nullable().optional(),
  /** The day the account last went Red, a `YYYY-MM-DD` calendar day. */
  red_health_date: z.string().nullable().optional(),
  /** The day the account last left Red, a `YYYY-MM-DD` calendar day. */
  left_red_health_date: z.string().nullable().optional(),
  /** Salesforce total contract value. */
  tcv: z.number().nullable().optional(),
  customer_slack_name: z.string().nullable().optional(),
  customer_slack_channel_id: z.string().nullable().optional(),
  internal_slack_channel: z.string().nullable().optional(),
  internal_slack_id: z.string().nullable().optional(),
});
export type AccountDetailRow = z.infer<typeof AccountDetailRowSchema>;

export const AccountsResponseSchema = z.object({
  rows: z.array(AccountRowSchema),
  next_cursor: z.string().nullable(),
});
export type AccountsResponse = z.infer<typeof AccountsResponseSchema>;

// ── Account detail envelope (drill-down) ─────────────────────────────────────
// GET /api/flow/accounts/:id returns any EXISTING, non-tombstoned mirror row —
// the VIEWABLE universe (plan D3/D4) — or, for a 001-shaped id with no mirror
// row at all, the account-less `'resolving'` member (plan D4/D10). A tombstoned
// row and a non-001 id with no row both stay a 404 (never this envelope).
//
// `state` is 'synced' for EVERY mirrored row, including an account nobody owns.
// What used to be the "Account not synced to Terra" dead-end panel was a
// visibility rule, not a sync fact — the bulk mirror refreshes all ~443k SFDC
// accounts hourly with no ownership predicate, so an unowned row is every bit as
// synced as an owned one (plan Problem Frame).
//
// `'not_synced'` is KEPT in the enum for one deploy cycle as skew tolerance: a
// new frontend deployed against an old backend still receives it, and renders
// it identically to 'synced'. Dropping it is tracked under the plan's "Deferred
// to Follow-Up Work".
//
// `in_list_universe` is the LIST-universe answer (`resolveAccountUniverseMembership`'s
// `inListUniverse`): false means "mirrored and fully viewable, but out of the
// ranked lists" — the getdimensionallynx.com case. OPTIONAL for the reverse
// skew (old backend, new frontend), where absent is treated as true.

/** The mirrored-row member: an account is present, `state` says how it got here. */
const FlowAccountDetailMirroredSchema = z.object({
  state: z.enum(['synced', 'not_synced']),
  account: AccountDetailRowSchema,
  in_list_universe: z.boolean().optional(),
});

/**
 * The force-fetch member (plan U4 / D10): no mirror row exists yet for a
 * 001-shaped id, an on-demand Middleman resolve has been fired, and the client
 * should poll this endpoint until it flips to `'synced'` (or gives up).
 *
 * `account` / `in_list_universe` are declared as phantom `undefined`-only keys
 * rather than omitted. That is deliberate and load-bearing for the union's
 * ergonomics: it keeps both fields *accessible* on the union type (as
 * `AccountRow | undefined`) so a consumer that reads `data.account` before
 * narrowing on `state` still type-checks, while parsing still REJECTS a
 * `'resolving'` payload that actually carries an account. They can be dropped
 * once every consumer narrows on `state` first.
 */
const FlowAccountDetailResolvingSchema = z.object({
  state: z.literal('resolving'),
  account: z.undefined().optional(),
  in_list_universe: z.undefined().optional(),
});

export const FlowAccountDetailResponseSchema = z.discriminatedUnion('state', [
  FlowAccountDetailMirroredSchema,
  FlowAccountDetailResolvingSchema,
]);
export type FlowAccountDetailResponse = z.infer<typeof FlowAccountDetailResponseSchema>;

// ── Opportunities (drill-down Opps tab) ──────────────────────────────────────
//
// U7 (Bucket B command-UX plan): widened for the six fixed persona roles +
// Next Step History + meta-chip counts. Persona ids are raw SFDC Contact ids
// (no FK — see core.prisma's `SalesforceOpportunity` comment); names are
// resolved server-side via ONE batched `SalesforceContact.findMany` across
// every opportunity in the response (not per-opp) — an unmatched id keeps
// the id and carries `name: null` rather than fabricating a label ("No
// Hardcoded Fallbacks" rule). Fill rates are sparse and vary widely by role
// (Champion ~35%, the rest ~3-9% — ask doc `docs/middleman-opportunity-
// personas-and-activities-ask.md`), so EVERY persona is `.nullable()`, not
// just the low-fill ones — a fully-empty opportunity is the empty state to
// design for, not the exception.

const OpportunityPersonaSchema = z.object({
  contact_id: z.string(),
  name: z.string().nullable(),
}).nullable();
export type OpportunityPersona = z.infer<typeof OpportunityPersonaSchema>;

/**
 * Fixed 6-role structure (Decision 7 — rendered as a fixed section, not
 * interleaved with timestamped activity). `economic_buyer` is
 * `Executive_Sponsor__c` — the SFDC field name and its UI label disagree
 * ("Economic Buyer" is the label Sales uses); the wire key follows the
 * label since that's what the UI actually needs to render.
 */
export const OpportunityPersonasSchema = z.object({
  champion: OpportunityPersonaSchema,
  economic_buyer: OpportunityPersonaSchema,
  decision_maker: OpportunityPersonaSchema,
  technical_stakeholder: OpportunityPersonaSchema,
  budget_holder: OpportunityPersonaSchema,
  primary_clay_user: OpportunityPersonaSchema,
});
export type OpportunityPersonas = z.infer<typeof OpportunityPersonasSchema>;

export const OpportunityRowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  stage: z.string().nullable(),
  amount: z.number().nullable(),
  close_date: z.string().nullable(),
  /** Needed client-side (and server-side, for the call-count window) to
   * bound the "account calls in deal window" query — Decision 8. */
  created_date: z.string().nullable(),
  type: z.string().nullable(),
  is_closed: z.boolean().nullable(),
  next_step: z.string().nullable(),
  /** Raw `Next_Step_History__c` text — parsed client-side by the shared
   * `parseNextStepHistory` (Decision 17, `src/shared/flow/next-step-
   * history.ts`). Sent raw so the client owns the tolerant-parse fallback. */
  next_step_history: z.string().nullable(),
  champion_status: z.string().nullable(),
  /** Denormalized display fields already on the SFDC object — no join. */
  primary_clay_user_name: z.string().nullable(),
  primary_clay_user_title: z.string().nullable(),
  personas: OpportunityPersonasSchema,
  /** Meta-chip counts, precomputed server-side so the collapsed row needs
   * zero extra fetches (Decision 6). */
  email_count: z.number(),
  call_count: z.number(),
});
export type OpportunityRow = z.infer<typeof OpportunityRowSchema>;

export const AccountOpportunitiesResponseSchema = z.object({
  opportunities: z.array(OpportunityRowSchema),
});

// ── Opportunity activity (lazy per-opp detail — Decision 6) ──────────────────
// GET /api/flow/accounts/:accountId/opportunities/:opportunityId/activity.
// Fetched on expand only; the list response above already carries the
// counts a collapsed row needs. Emails are deal-scoped via `account_activity.
// what_id` (U2); calls are account-scoped and date-windowed (Decision 8 — no
// per-opportunity Gong object sync exists) — see the handler for the exact
// predicate and window.

export const OpportunityActivityEmailSchema = z.object({
  /** `account_activity.source_id` — the SFDC Task id; the client builds the
   * link via `sfdcRecordUrl('Task', source_id)`. */
  source_id: z.string(),
  subject: z.string().nullable(),
  description: z.string().nullable(),
  owner_name: z.string().nullable(),
  contact_name: z.string().nullable(),
  effective_at: z.string().nullable(),
});
export type OpportunityActivityEmail = z.infer<typeof OpportunityActivityEmailSchema>;

export const OpportunityActivityCallParticipantSchema = z.object({
  party_name: z.string(),
  affiliation: z.string().nullable(),
});

export const OpportunityActivityCallSchema = z.object({
  call_id: z.string(),
  call_title: z.string(),
  started_at: z.string(),
  participants: z.array(OpportunityActivityCallParticipantSchema),
});
export type OpportunityActivityCall = z.infer<typeof OpportunityActivityCallSchema>;

export const OpportunityActivityResponseSchema = z.object({
  emails: z.array(OpportunityActivityEmailSchema),
  calls: z.array(OpportunityActivityCallSchema),
});
export type OpportunityActivityResponse = z.infer<typeof OpportunityActivityResponseSchema>;

// ── Reusable cross-dimension FilterGroups (Rob's asks) ───────────────────────
// These are ordinary VISIBLE filter groups, not a hidden toggle layer: whoever
// seeds one is seeding a row the rep can read in the Filters popover and
// delete. `CHIP_NOT_AGENCY` and `CHIP_NO_OPEN_OPP` are live today — the ClayDR
// account landing default composes them (web/app-home/src/components/flow/
// roleDefaults.ts). `CHIP_UNASSIGNED_GTME` has no consumer yet and is kept here
// (validated by flow-account-contract.test.ts) so its exact FilterGroup is
// ready when one appears.
//
// SUPERSEDED (do not restore): an earlier comment here planned to "seed these
// as default saved views" once saved views landed. That plan is explicitly out
// of scope — seeded "No open opp" / "Unassigned GTME" default views are on the
// DO-NOT-BUILD list (Flow Custom Views plan, Scope Boundaries). Saved views
// (U6) are rep-authored; nothing auto-seeds a view row.

/**
 * "No open opp" — the SFDC formula is null (not 0) when there are no open
 * opps, so match `= 0` OR empty; a bare `= 0` would miss the null band.
 */
export const CHIP_NO_OPEN_OPP: FilterGroup = {
  id: 'chip-no-open-opp',
  logic: 'OR',
  conditions: [
    { id: 'chip-noo-1', field: 'number_of_open_opp', operator: 'equals', value: 0 },
    { id: 'chip-noo-2', field: 'number_of_open_opp', operator: 'is_empty', value: null },
  ],
};

/**
 * "Not an agency" — `is_agency` equals false. The SQL builder coalesces a
 * boolean NULL to false (condition-to-sql.ts), so this single condition also
 * matches accounts with no agency flag set — no OR-null band needed.
 */
export const CHIP_NOT_AGENCY: FilterGroup = {
  id: 'chip-not-agency',
  logic: 'AND',
  conditions: [
    // Boolean filter values travel as strings over the wire (FilterCondition's
    // value union is string|string[]|number|null); the SQL builder reads
    // 'true'/'false'. 'false' → COALESCE(sa.is_agency, false) = false.
    { id: 'chip-agency-1', field: 'is_agency', operator: 'equals', value: 'false' },
  ],
};

/** "Unassigned GTME" — GTME Owner = the sf_owner_id join (Views doc 3927). */
export const CHIP_UNASSIGNED_GTME: FilterGroup = {
  id: 'chip-unassigned-gtme',
  logic: 'AND',
  conditions: [
    { id: 'chip-gtme-1', field: 'owner_name', operator: 'is_empty', value: null },
  ],
};

// ── Seeded presets (v0 — code-defined, no DB) ────────────────────────────────

/**
 * Default column set — the FULL default-view field list from the Views doc
 * (3927), riding along in every account view. Presets front-load after Name.
 */
export const ACCOUNT_DEFAULT_COLUMNS = [
  // Identity & rank (Domain + LinkedIn ride next to Propensity per QA)
  'name', 'propensity_score', 'email_domain_link', 'linkedin', 'fit_score', 'arr',
  // Coverage
  'owner_name', 'claydr_name', 'gs_name',
  'last_gtme_meeting', 'last_gs_meeting', 'last_claydr_meeting',
  // GTME/CDR meeting links (U6) — customer variants are catalog-only.
  'owner_meeting_link', 'claydr_meeting_link',
  // Commercial
  'product_tier', 'plan_interval', 'number_of_open_opp',
  // Last Interesting Moment
  'lim_date', 'lim_name', 'lim_type', 'lim_subtype', 'lim_description', 'lim_link',
  // Product & signals
  'number_of_workspace', 'workspaces_created_last_90_days',
  'l90_signal_count', 'l90_website_visits', 'l90_active_clay_users',
  // Firmographics & tech
  'number_of_employees', 'tech_stack', 'technologies_used',
  // Links
  'sfdc_link',
];

/** Column composition rule: Name (pinned) → front-loaded → remaining defaults. */
export function composeAccountColumns(frontLoaded: string[]): string[] {
  const rest = ACCOUNT_DEFAULT_COLUMNS.filter(c => c !== 'name' && !frontLoaded.includes(c));
  return ['name', ...frontLoaded.filter(c => c !== 'name'), ...rest];
}

// No `scope` (U5 / R2). Command Center rankings are a cross-book prospecting
// surface, not "my book": a ClayDR (or anyone) must see accounts they're
// assigned to but don't OWN (e.g. an account whose SFDC owner is an AE but
// whose ClayDR is the rep). Presets therefore describe the whole account
// universe, and per-role narrowing is a VISIBLE default filter row
// (web/app-home/src/components/flow/roleDefaults.ts) the rep can clear.
//
// The one narrowing that is NOT a filter row is the accounts bulk-provenance
// guard (`sa.source <> 'sfdc-bulk' OR owned-by-anyone`) in the list handler's
// base WHERE — that defines which rows exist in Flow's universe at all
// (unsynced object-sync stubs), which is data provenance, not a per-rep
// preference. See Decision 8 in the Custom Views plan.
// `groupBy` defaults to `[]` (no grouping) so every OTHER preset below can
// keep omitting it — only the Tracker preset sets a real value. See the
// identical comment on contact-view.ts's `preset()`.
function preset(p: Omit<FlowView, 'createdBy' | 'entity' | 'groupBy'> & { groupBy?: string[] }): FlowView {
  return { createdBy: null, entity: 'account', groupBy: [], ...p };
}

/** The `product_tier` value an Enterprise customer carries in the account mirror. */
const ENTERPRISE_PRODUCT_TIER = 'Enterprise';

/** The Growth Strategist presets in `ACCOUNT_PRESETS`. */
export const GS_MY_BOOK_PRESET_ID = 'gs-my-book';
export const GS_ALL_ENTERPRISE_PRESET_ID = 'gs-all-enterprise';

/**
 * The Growth Strategist presets. They are offered to every viewer, like any
 * other preset, and they are complete as written, so role defaults never seed
 * onto them (`withRoleDefaults`). Only a real gs identity lands on GS My Book
 * (`accountLandingPresetId`).
 */
export const GS_PRESET_IDS: ReadonlySet<string> = new Set([GS_MY_BOOK_PRESET_ID, GS_ALL_ENTERPRISE_PRESET_ID]);

export const ACCOUNT_PRESETS: FlowView[] = [
  preset({
    id: 'account-propensity',
    name: 'By Propensity',
    filter: [],
    sort: { field: 'propensity_score', dir: 'DESC' },
    columns: composeAccountColumns([]),
    isDefault: true,
  }),
  preset({
    id: 'account-engagement',
    name: 'By Engagement',
    filter: [],
    sort: { field: 'engagement_score_90_days', dir: 'DESC' },
    columns: composeAccountColumns(['engagement_score_90_days', 'engagement_status_90_days', 'l90_website_visits', 'l90_active_clay_users']),
    isDefault: false,
  }),
  // Tracker (U7 / R6 — the contacts prototype's `p-track`, mirrored onto
  // accounts). No tracker FILTER: the view buckets the whole universe, so
  // untracked accounts appear under 'No status'. Due → Starred → Reminders →
  // Snoozed → No status with the most overdue first comes from the
  // `groupBy: 'tracker'` prefix (tracker_rank ASC, tracker_date ASC); the sort
  // below is the within-bucket fallback where dates tie at null.
  preset({
    id: 'account-tracker',
    name: 'Tracker',
    filter: [],
    sort: { field: 'engagement_score_90_days', dir: 'DESC' },
    groupBy: ['tracker'],
    // U7: `tracker` dropped from columns — see the identical note in
    // contact-view.ts's `contact-tracker` preset.
    columns: composeAccountColumns(['tracker_date', 'tracker_note']),
    isDefault: false,
  }),
  preset({
    id: 'account-lim',
    name: 'By Last Interesting Moment',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'lim_date', operator: 'is_not_empty', value: null }],
    }],
    sort: { field: 'lim_date', dir: 'DESC' },
    columns: composeAccountColumns([
      // lim_link leads: the Signal ↗ click-through must not need a scroll.
      'lim_link', 'lim_date', 'lim_name', 'lim_type', 'lim_subtype', 'lim_description',
    ]),
    isDefault: false,
  }),
  // Top-level Fortune 500 companies in rank order, rank 1 first. Salesforce copies a
  // parent's rank onto its children, so the two hierarchy rows (no parent, no ultimate
  // parent) keep one row per company; delete them to see children. The sort is backed by `salesforce_account_fortune_500_rank_sort_idx`.
  preset({
    id: 'account-f500',
    name: 'F500 Rank',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [
        { id: 'c1', field: 'fortune_500_rank', operator: 'is_not_empty', value: null },
        { id: 'c2', field: 'parent_account_id', operator: 'is_empty', value: null },
        { id: 'c3', field: 'ultimate_global_parent_id', operator: 'is_empty', value: null },
      ],
    }],
    sort: { field: 'fortune_500_rank', dir: 'ASC' },
    columns: composeAccountColumns(['fortune_500_rank']),
    isDefault: false,
  }),
  // The two presets below are `GS_PRESET_IDS`, offered to every viewer. GS My Book's one
  // group is the gs `{me}` chip itself, under the chip's own group id.
  preset({
    id: GS_MY_BOOK_PRESET_ID,
    flowLayout: 'gs',
    name: 'GS My Book',
    filter: [meChipGroup('gs', 'account')],
    sort: { field: 'contract_end', dir: 'ASC' },
    // The parked ledger's six columns, then Next GS and Next GS Date, sorted by
    // contract end. `composeAccountColumns` would append the rest of the
    // default columns.
    columns: [...GS_LEDGER_DEFAULT_COLUMN_KEYS],
    isDefault: false,
  }),
  preset({
    id: GS_ALL_ENTERPRISE_PRESET_ID,
    flowLayout: 'gs',
    name: 'GS All Enterprise Customers',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'product_tier', operator: 'equals', value: ENTERPRISE_PRODUCT_TIER }],
    }],
    sort: { field: 'contract_end', dir: 'ASC' },
    // The parked ledger's six columns, then Next GS and Next GS Date, sorted by
    // contract end. `composeAccountColumns` would append the rest of the
    // default columns.
    columns: [...GS_LEDGER_DEFAULT_COLUMN_KEYS],
    isDefault: false,
  }),
];

export const ACCOUNT_DEFAULT_PRESET = ACCOUNT_PRESETS.find(p => p.isDefault)!;

/**
 * Landing preset for every Flow role except gs. A Growth Strategist lands on
 * GS My Book instead, see `accountLandingPresetId`. Deliberately a SEPARATE
 * id from `ACCOUNT_DEFAULT_PRESET.id` ('account-propensity', `isDefault:
 * true`) rather than an `isDefault` flip. `isDefault` means "this mount's
 * canonical baseline" and other code may come to rely on that meaning later,
 * so it stays put. The landing id only feeds the LAST rung of the landing
 * precedence in `useViewStateMachine` (`?view=` → stored active → stored
 * default pointer → this code-defined landing id). It does not touch
 * `isDefault` on any preset.
 */
export const ACCOUNT_LANDING_PRESET_ID = 'account-tracker';

/** The landing preset for a viewer's Flow role. A Growth Strategist lands on GS My Book. */
export function accountLandingPresetId(flowRole: FlowRole | null | undefined): string {
  return flowRole === 'gs' ? GS_MY_BOOK_PRESET_ID : ACCOUNT_LANDING_PRESET_ID;
}
